import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { subscriptionService, authService, plantService, userService, productService, machineService, lineService, masterDataService, dashboardService } from '../services';
import { usePermissions } from '../hooks/usePermissions';
import { useNavigationAccess } from '../navigation/useNavigationAccess';
import { flattenNavForPalette } from '../navigation/flattenNavForPalette';
import HomeModuleLauncher from '../components/HomeModuleLauncher';
import TenantOnboardingGuide from '../components/TenantOnboardingGuide';
import { useTenantOnboardingStatus } from '../hooks/useTenantOnboardingStatus';
import PageLoading from "../components/common/PageLoading";
import './DashboardHome.css';

const CACHE_KEY = 'dashboard_usage_summary';
const CACHE_TIMESTAMP_KEY = 'dashboard_usage_summary_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Synchronous cache loading function
const getCachedDashboardData = () => {
  try {
    const cachedData = sessionStorage.getItem(CACHE_KEY);
    const cachedTimestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (cachedData && cachedTimestamp) {
      const cacheAge = Date.now() - parseInt(cachedTimestamp, 10);
      if (cacheAge < CACHE_DURATION) {
        return {
          data: JSON.parse(cachedData),
          hasData: true
        };
      }
    }
    return { data: null, hasData: false };
  } catch (e) {
    console.error('Error parsing cached dashboard data:', e);
    return { data: null, hasData: false };
  }
};

const DashboardHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { ctx, featuresLoading } = useNavigationAccess();
  const paletteEntries = useMemo(() => {
    if (featuresLoading) return [];
    return flattenNavForPalette(ctx);
  }, [ctx, featuresLoading]);

  const onboarding = useTenantOnboardingStatus({
    paletteEntries,
    featuresLoading,
  });
  const isInitialMount = useRef(true);

  const initialDashboardCacheRef = useRef(null);
  if (initialDashboardCacheRef.current === null) {
    initialDashboardCacheRef.current = getCachedDashboardData();
  }
  const cachedInitialData = initialDashboardCacheRef.current;

  // Permission check (memoized — avoids churning nav-dependent children)
  const canView = useMemo(
    () => hasPermission('Dashboard.View'),
    [hasPermission],
  );

  const [usageSummary, setUsageSummary] = useState(() => cachedInitialData.data);
  const [loading, setLoading] = useState(() => !cachedInitialData.hasData);
  const [error, setError] = useState('');
  const [workspaceOverviewNavKeys, setWorkspaceOverviewNavKeys] = useState(null);
  const [workspaceOverviewLoading, setWorkspaceOverviewLoading] = useState(true);

  // Load fresh data in background
  useEffect(() => {
    if (canView) {
      // Always refresh data in the background
      loadUsageSummary(!cachedInitialData.hasData);

      // Preload common data for quick actions (non-blocking)
      preloadCommonData();
    } else {
      setError('You do not have permission to view the dashboard');
      setLoading(false);
      setWorkspaceOverviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await dashboardService.getWorkspaceOverview();
        const keys = data?.navKeys;
        if (!cancelled) {
          if (Array.isArray(keys) && keys.length > 0) {
            setWorkspaceOverviewNavKeys(keys);
          } else {
            setWorkspaceOverviewNavKeys(null);
          }
        }
      } catch (e) {
        console.error('Error loading workspace overview config:', e);
        if (!cancelled) setWorkspaceOverviewNavKeys(null);
      } finally {
        if (!cancelled) setWorkspaceOverviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  // Reload data when navigating back to dashboard
  useEffect(() => {
    if (location.pathname === '/home' && !isInitialMount.current && canView) {
      // Only reload if we don't have fresh cached data
      const cachedTimestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);
      if (cachedTimestamp) {
        const cacheAge = Date.now() - parseInt(cachedTimestamp, 10);
        if (cacheAge >= CACHE_DURATION) {
          loadUsageSummary(false);
        }
      }
    }
  }, [location.pathname, canView]);

  const loadUsageSummary = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const tenantId = authService.getTenantId();
      if (tenantId) {
        const summary = await subscriptionService.getUsageSummary(tenantId);
        setUsageSummary(summary);

        // Cache the data
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(summary));
        sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      }
    } catch (err) {
      setError('Failed to load usage summary');
      console.error('Error loading usage summary:', err);
      // Don't clear cached data on error, keep showing it
      if (!cachedInitialData.hasData) {
        setUsageSummary(null);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      isInitialMount.current = false;
    }
  };

  // Preload common data for quick action pages (runs in background)
  const preloadCommonData = async () => {
    try {
      const tenantId = authService.getTenantId();
      if (!tenantId) return;

      // Check if data is already cached and fresh (less than 2 minutes old)
      const cacheTimestamp = sessionStorage.getItem('common_data_timestamp');
      if (cacheTimestamp) {
        const cacheAge = Date.now() - parseInt(cacheTimestamp, 10);
        if (cacheAge < 2 * 60 * 1000) {
          // Data is fresh, skip preloading
          return;
        }
      }

      // Preload data in parallel (non-blocking, no error handling needed)
      Promise.all([
        plantService.getAllPlants().then(data => {
          if (data) sessionStorage.setItem('plants_data', JSON.stringify(data));
        }).catch(() => { }),
        userService.getAllUsers().then(data => {
          if (data) sessionStorage.setItem('users_data', JSON.stringify(data));
        }).catch(() => { }),
        productService.getAllProducts().then(data => {
          if (data) sessionStorage.setItem('products_data', JSON.stringify(data));
        }).catch(() => { }),
        machineService.getAllMachines().then(data => {
          if (data) sessionStorage.setItem('machines_data', JSON.stringify(data));
        }).catch(() => { }),
        lineService.getAllLines().then(data => {
          if (data) sessionStorage.setItem('lines_data', JSON.stringify(data));
        }).catch(() => { }),
        masterDataService.getShifts?.(tenantId).then(data => {
          if (data) sessionStorage.setItem('shifts_data', JSON.stringify(data));
        }).catch(() => { })
      ]).then(() => {
        sessionStorage.setItem('common_data_timestamp', Date.now().toString());
      }).catch(() => { });
    } catch (err) {
      // Silently fail - preloading is optional
      console.debug('Error preloading common data:', err);
    }
  };

  const getUsagePercentage = (current, max) => {
    if (max === 0) return 0; // Unlimited
    return Math.min((current / max) * 100, 100);
  };

  if (loading) {
    return (
      <div className="dashboard-home">
        <PageLoading message="Loading home..." />
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="dashboard-home">
        <div className="error-message" style={{
          textAlign: 'center',
          padding: '2rem',
          fontSize: '1.1rem',
          color: '#d93025'
        }}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view the home page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-home">
      <div className="welcome-section">
        <h2>Welcome back, {authService.getUser()?.fullName || 'User'}!</h2>
        <p>Here's an overview of your plant maintenance system</p>
      </div>

      <TenantOnboardingGuide
        guideLoading={onboarding.guideLoading}
        steps={onboarding.steps}
        allDone={onboarding.allDone}
        percentComplete={onboarding.percentComplete}
        completedCount={onboarding.completedCount}
        totalCount={onboarding.totalCount}
        refetch={onboarding.refetch}
      />

      <HomeModuleLauncher
        entries={paletteEntries}
        featuresLoading={featuresLoading}
        workspaceOverviewNavKeys={workspaceOverviewNavKeys}
      />

      {error && <div className="error-message">{error}</div>}

      {usageSummary && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-icon"><svg xmlns="http://www.w3.org/2000/svg" id="Layer_1" data-name="Layer 1" viewBox="0 0 24 24">
                <path d="m16,10.196v-5l-8,5V2h1V0H0v2h1v19c0,1.654,1.346,3,3,3h17c1.654,0,3-1.346,3-3V5.196l-8,5Zm6,10.804c0,.551-.448,1-1,1H4c-.552,0-1-.449-1-1V2h3v17h2v-6.445l6-3.75v5l8-5v12.196Zm-11-5h3v3h-3v-3Zm8,3h-3v-3h3v3Z" />
              </svg></span>
              <h3>Plants</h3>
            </div>
            <div className="stat-value">
              {usageSummary.currentUsage?.plants || 0}
              {usageSummary.limits?.maxPlants > 0 && (
                <span className="stat-limit"> / {usageSummary.limits?.maxPlants}</span>
              )}
              <span class="stat-badge positive">↗ 25.4%</span>
            </div>
            {usageSummary.limits?.maxPlants > 0 && (
              <div className="stat-progress">
                <div
                  className="stat-progress-bar"
                  style={{
                    width: `${getUsagePercentage(
                      usageSummary.currentUsage?.plants || 0,
                      usageSummary.limits?.maxPlants
                    )}%`,
                  }}
                ></div>
              </div>
            )}
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-icon"> <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M12.0001 1.25C9.37678 1.25 7.25013 3.37665 7.25013 6C7.25013 8.62335 9.37678 10.75 12.0001 10.75C14.6235 10.75 16.7501 8.62335 16.7501 6C16.7501 3.37665 14.6235 1.25 12.0001 1.25ZM8.75013 6C8.75013 4.20507 10.2052 2.75 12.0001 2.75C13.7951 2.75 15.2501 4.20507 15.2501 6C15.2501 7.79493 13.7951 9.25 12.0001 9.25C10.2052 9.25 8.75013 7.79493 8.75013 6Z" fill="" />
                <path fill-rule="evenodd" clip-rule="evenodd" d="M12.0001 12.25C9.68658 12.25 7.55506 12.7759 5.97558 13.6643C4.41962 14.5396 3.25013 15.8661 3.25013 17.5L3.25007 17.602C3.24894 18.7638 3.24752 20.222 4.52655 21.2635C5.15602 21.7761 6.03661 22.1406 7.22634 22.3815C8.4194 22.6229 9.97436 22.75 12.0001 22.75C14.0259 22.75 15.5809 22.6229 16.7739 22.3815C17.9637 22.1406 18.8443 21.7761 19.4737 21.2635C20.7527 20.222 20.7513 18.7638 20.7502 17.602L20.7501 17.5C20.7501 15.8661 19.5807 14.5396 18.0247 13.6643C16.4452 12.7759 14.3137 12.25 12.0001 12.25ZM4.75013 17.5C4.75013 16.6487 5.37151 15.7251 6.71098 14.9717C8.02693 14.2315 9.89541 13.75 12.0001 13.75C14.1049 13.75 15.9733 14.2315 17.2893 14.9717C18.6288 15.7251 19.2501 16.6487 19.2501 17.5C19.2501 18.8078 19.2098 19.544 18.5265 20.1004C18.156 20.4022 17.5366 20.6967 16.4763 20.9113C15.4194 21.1252 13.9744 21.25 12.0001 21.25C10.0259 21.25 8.58087 21.1252 7.52393 20.9113C6.46366 20.6967 5.84425 20.4022 5.47372 20.1004C4.79045 19.544 4.75013 18.8078 4.75013 17.5Z" fill="" />
              </svg></span>
              <h3>Users</h3>
            </div>
            <div className="stat-value">
              {usageSummary.currentUsage?.users || 0}
              {usageSummary.limits?.maxUsers > 0 && (
                <span className="stat-limit"> / {usageSummary.limits?.maxUsers}</span>
              )}
              <span class="stat-badge negative">↘ 12.6%</span>
            </div>
            {usageSummary.limits?.maxUsers > 0 && (
              <div className="stat-progress">
                <div
                  className="stat-progress-bar"
                  style={{
                    width: `${getUsagePercentage(
                      usageSummary.currentUsage?.users || 0,
                      usageSummary.limits?.maxUsers
                    )}%`,
                  }}
                ></div>
              </div>
            )}
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-icon">
                <img src='cog.svg' />
              </span>
              <h3>Machines</h3>
            </div>
            <div className="stat-value">
              {usageSummary.currentUsage?.machines || 0}
              {usageSummary.limits?.maxMachines > 0 && (
                <span className="stat-limit"> / {usageSummary.limits?.maxMachines}</span>
              )}
              <span class="stat-badge positive">↗ 3.1%</span>
            </div>
            {usageSummary.limits?.maxMachines > 0 && (
              <div className="stat-progress">
                <div
                  className="stat-progress-bar"
                  style={{
                    width: `${getUsagePercentage(
                      usageSummary.currentUsage?.machines || 0,
                      usageSummary.limits?.maxMachines
                    )}%`,
                  }}
                ></div>
              </div>
            )}
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-icon">
                <img src='iconmail.svg' />
              </span>
              <h3>Subscription</h3>
            </div>
            <div className='flexbox'>
              <div className="stat-value fnt15">
                {usageSummary.currentPlan?.planName || 'N/A'}
              </div>
              <div className="">
                {usageSummary.subscriptionStatus === 'Active' ? (
                  <span className="stat-badge active">Active</span>
                ) : (
                  <span className="stat-badge negative">{usageSummary.subscriptionStatus}</span>
                )}

              </div>
            </div>
          </div>
        </div>
      )}

      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <button className="action-card" onClick={() => navigate('/home/plants')}>
            <span className="action-icon">
              <img src='iconplussvg.svg' />
            </span>
            <span className="action-label">Add Plant</span>
          </button>
          <button className="action-card" onClick={() => navigate('/home/machines')}>
            <span className="action-icon">
              <img src='cog.svg' />
            </span>
            <span className="action-label">Add Machine</span>
          </button>
          <button className="action-card" onClick={() => navigate('/home/oee-entry')}>
            <span className="action-icon">
              <img src='data.svg' />
            </span>
            <span className="action-label">Enter OEE Data</span>
          </button>
          <button className="action-card" onClick={() => navigate('/home/oee')}>
            <span className="action-icon">
              <img src='chart.svg' />
            </span>
            <span className="action-label">View OEE Analytics</span>
          </button>
          <button className="action-card" onClick={() => navigate('/home/ai-dashboard')}>
            <span className="action-icon">
              🤖
            </span>
            <span className="action-label">MaX Elite AI</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;

